import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { api, formatDate, formatEUR, DOSSIER_STATUSES } from "@/lib/api";
import { PageHeader, SelectField, EmptyState } from "@/components/ui-kit";
import StatusBadge from "@/components/StatusBadge";
import { Briefcase, Search } from "lucide-react";

export default function Dossiers() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statutFilter, setStatutFilter] = useState("");
  const [search, setSearch] = useState("");
  const [params] = useSearchParams();

  useEffect(() => {
    // preset filter from URL
    const s = params.get("statut") || "";
    const f = params.get("facture_statut") || "";
    const fin = params.get("financement_statut") || "";
    const denonc = params.get("denonciation_statut") || "";
    const opt = params.get("option_statut") || "";
    const q = {};
    if (s) q.statut = s;
    if (f) q.facture_statut = f;
    if (fin) q.financement_statut = fin;
    if (denonc) q.denonciation_statut = denonc;
    if (opt) q.option_statut = opt;
    if (statutFilter) q.statut = statutFilter;
    if (search) q.search = search;
    setLoading(true);
    api.get("/dossiers", { params: q }).then((r) => setItems(r.data)).finally(() => setLoading(false));
  }, [statutFilter, search, params]);

  return (
    <div className="p-8" data-testid="dossiers-page">
      <PageHeader title="Dossiers signés" subtitle="Espace Admin / BackOffice" />

      <div className="bg-white rounded-sm border border-slate-200 mb-6 p-4 flex flex-wrap gap-3 items-end">
        <div className="flex-1 min-w-[200px] relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Rechercher (client, promoteur, programme)" data-testid="dossiers-search-input"
            className="w-full pl-9 pr-3 py-2 border border-slate-300 rounded-sm focus:outline-none focus:ring-1 focus:ring-[#111111] text-sm" />
        </div>
        <div className="min-w-[220px]">
          <SelectField testid="dossiers-status-filter" value={statutFilter} onChange={(e) => setStatutFilter(e.target.value)}
            options={[{ value: "", label: "Tous les statuts" }, ...DOSSIER_STATUSES]} />
        </div>
      </div>

      <div className="bg-white rounded-sm border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-500">Chargement…</div>
        ) : items.length === 0 ? (
          <EmptyState icon={Briefcase} title="Aucun dossier signé" description="Les dossiers apparaîtront ici automatiquement après signature d'un prospect." testid="dossiers-empty" />
        ) : (
          <table className="w-full text-sm" data-testid="dossiers-table">
            <thead className="bg-slate-50 text-slate-700">
              <tr>
                <th className="text-left px-4 py-3 font-semibold uppercase text-xs tracking-wider">Client</th>
                <th className="text-left px-4 py-3 font-semibold uppercase text-xs tracking-wider">Programme / Lot</th>
                <th className="text-left px-4 py-3 font-semibold uppercase text-xs tracking-wider">Promoteur</th>
                <th className="text-left px-4 py-3 font-semibold uppercase text-xs tracking-wider">Statut</th>
                <th className="text-left px-4 py-3 font-semibold uppercase text-xs tracking-wider">Financement</th>
                <th className="text-left px-4 py-3 font-semibold uppercase text-xs tracking-wider">Facture</th>
                <th className="text-right px-4 py-3 font-semibold uppercase text-xs tracking-wider">Commission</th>
                <th className="text-left px-4 py-3 font-semibold uppercase text-xs tracking-wider">Signé le</th>
              </tr>
            </thead>
            <tbody>
              {items.map((d) => (
                <tr key={d.id} className="border-t border-slate-100 hover:bg-slate-50">
                  <td className="px-4 py-3">
                    <Link to={`/dossiers/${d.id}`} className="font-semibold text-[#111111] hover:text-[#B8873A]" data-testid={`dossier-link-${d.id}`}>
                      {d.client_prenom} {d.client_nom}
                    </Link>
                    <div className="text-xs text-slate-500">{d.client_email}</div>
                  </td>
                  <td className="px-4 py-3">
                    <div>{d.programme || "—"}</div>
                    <div className="text-xs text-slate-500">Lot {d.lot || "—"}</div>
                  </td>
                  <td className="px-4 py-3">{d.promoteur || "—"}</td>
                  <td className="px-4 py-3"><StatusBadge value={d.statut} kind="dossier" /></td>
                  <td className="px-4 py-3"><StatusBadge value={d.financement_statut} kind="financement" /></td>
                  <td className="px-4 py-3"><StatusBadge value={d.facture_statut} kind="facture" /></td>
                  <td className="px-4 py-3 text-right font-medium">{formatEUR(d.commission_montant)}</td>
                  <td className="px-4 py-3 text-slate-500">{formatDate(d.date_signature)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
